import {
  type NativeNamespace,
  readNativeNamespace,
} from "../private/nativeBridge";
import type { BridgeReadyState, BridgeTiming } from "../types";

// A sampler, not a poller. There is deliberately no setInterval /
// requestAnimationFrame / MutationObserver here: a library that exists to
// measure page performance must not spend main-thread time doing it. Each call
// happens on a code path that is already running (an analytics flush), so the
// observation is free. What it costs instead is resolution — see BridgeTiming.

const STATE_RANK: Record<BridgeReadyState, number> = {
  absent: 0,
  partial: 1,
  ready: 2,
};

function observeFeature(
  namespace: NativeNamespace | undefined,
  feature: "onQueried" | "onMessage",
): BridgeReadyState {
  if (namespace === undefined || namespace === null) return "absent";
  try {
    return typeof namespace[feature] === "function" ? "ready" : "partial";
  } catch {
    // The namespace itself was observable, the feature was not.
    return "partial";
  }
}

/**
 * Builds an independent sampler with its own observation state.
 *
 * Each returned call reads `window.native` once and folds the observation into
 * a monotonic running state, then returns the whole accumulated
 * {@link BridgeTiming} row — the row is complete on every call, so any flush
 * can carry the latest one and downstream just takes the last row per trace.
 */
export function createBridgeStateSampler(): () => BridgeTiming {
  let sampleCount = 0;
  let driverState: BridgeReadyState = "absent";
  let driverReadyTime: number | undefined;
  let instanceState: BridgeReadyState = "absent";
  let instanceReadyTime: number | undefined;

  return function sampleBridgeState(): BridgeTiming {
    const now = performance.now();
    sampleCount += 1;

    const namespace = readNativeNamespace();
    const observedDriver = observeFeature(namespace, "onQueried");
    const observedInstance = observeFeature(namespace, "onMessage");

    // Upgrade only. A namespace that disappears mid-page (teardown, a host
    // page reassigning window.native) must not rewrite an observation that
    // already happened.
    if (STATE_RANK[observedDriver] > STATE_RANK[driverState]) {
      driverState = observedDriver;
      if (observedDriver === "ready") driverReadyTime = now;
    }
    if (STATE_RANK[observedInstance] > STATE_RANK[instanceState]) {
      instanceState = observedInstance;
      if (observedInstance === "ready") instanceReadyTime = now;
    }

    const timing: BridgeTiming = {
      driver_ready_state: driverState,
      bridge_instance_ready_state: instanceState,
      observation_end_time: now,
      // "ready" only when BOTH surfaces were seen ready: a driver without the
      // response listener is exactly the window the incident happened in.
      stop_reason:
        driverState === "ready" && instanceState === "ready"
          ? "ready"
          : "page_end",
      poll_count: sampleCount,
    };
    if (driverReadyTime !== undefined) {
      timing.driver_ready_time = driverReadyTime;
    }
    if (instanceReadyTime !== undefined) {
      timing.bridge_instance_ready_time = instanceReadyTime;
    }
    return timing;
  };
}

// Page-scoped singleton for the IIFE entry point. Tests build their own with
// createBridgeStateSampler() rather than resetting this one.
export const sampleBridgeState = createBridgeStateSampler();
